import { isReviewItemResolved } from "@utils/reportVisibility";
import { useCallback } from "react";

import { ReviewItemFieldsFragment } from "@api/graphql/types-and-hooks";
import { getSuggestionIcon } from "@components/QaReview/components/suggestionIcon";
import { useReviewData } from "@components/QaReview/hooks/useReviewData";
import { DEFAULT_REVIEW_VIEW, ReviewView, useReviewSession } from "@components/QaReview/hooks/useReviewSession";
import { isReportAuthor } from "@components/ReportReview/hooks/useCanManageReview";
import { useMyAuth0Id } from "@components/ReportReview/hooks/useMyAuth0Id";
import { useReportOptional } from "@providers/ReportProvider";

/** The label a QA suggestion is grouped under, shared by the view picker's rows and the name of a picked category. */
export const qaCategoryLabel = (item: ReviewItemFieldsFragment): string => {
  if (item.__typename !== "ReportSuggestionType") return "";
  return getSuggestionIcon(item.category ?? "").label.trim() || "Other";
};

/** Categories match whatever their casing, so "CCS QA" and "CCS_QA" are one view. */
export const qaCategoryKey = (label: string): string => label.toLowerCase();

/** Who is looking, for Waiting on you. */
export type ReviewViewer = {
  myId: string | undefined;
  isReportAuthor: boolean;
  repliesByParentId: Map<string, ReviewItemFieldsFragment[]>;
};

/**
 * An open Team Review thread where someone else spoke last. The report author answers every thread; anyone
 * else only the threads they started or replied in.
 */
export const isWaitingOnViewer = (root: ReviewItemFieldsFragment, viewer: ReviewViewer): boolean => {
  if (root.__typename !== "ReportCommentType" || isReviewItemResolved(root) || !viewer.myId) return false;
  const thread = [root, ...(viewer.repliesByParentId.get(root.id) ?? [])];
  const authorId = (comment: ReviewItemFieldsFragment) =>
    comment.__typename === "ReportCommentType" ? comment.author?.id : undefined;
  if (authorId(thread[thread.length - 1]) === viewer.myId) return false;
  return viewer.isReportAuthor || thread.some((comment) => authorId(comment) === viewer.myId);
};

export const isReviewItemListed = (item: ReviewItemFieldsFragment, view: ReviewView, viewer: ReviewViewer): boolean => {
  const isResolved = isReviewItemResolved(item);
  const isQa = item.__typename === "ReportSuggestionType";
  switch (view.kind) {
    case "open":
      return !isResolved;
    case "peer":
      return !isResolved && !isQa;
    case "qa":
      return (
        !isResolved && isQa && (!view.category || qaCategoryKey(qaCategoryLabel(item)) === qaCategoryKey(view.category))
      );
    case "waiting":
      return isWaitingOnViewer(item, viewer);
    case "resolved":
      return isResolved;
  }
};

/** The view that lists an item, for jumping to one the current view leaves out. */
export const viewListing = (item: ReviewItemFieldsFragment): ReviewView =>
  isReviewItemResolved(item) ? { kind: "resolved" } : DEFAULT_REVIEW_VIEW;

/** The view's name where it is shown, e.g. on the navigator. */
export const reviewViewLabel = (view: ReviewView): string => {
  switch (view.kind) {
    case "open":
      return "All open";
    case "peer":
      return "Team Review";
    case "qa":
      return view.category || "Quality Review";
    case "waiting":
      return "Waiting on you";
    case "resolved":
      return "Resolved";
  }
};

export const useReviewViewer = (): ReviewViewer => {
  const myId = useMyAuth0Id();
  const report = useReportOptional()?.report;
  const { repliesByParentId } = useReviewData();
  return { myId, isReportAuthor: isReportAuthor(report, myId), repliesByParentId };
};

/** Whether the Checkpoint column lists an item under the current view. */
export const useIsReviewItemListed = (): ((item: ReviewItemFieldsFragment) => boolean) => {
  const { state } = useReviewSession();
  const { myId, isReportAuthor, repliesByParentId } = useReviewViewer();
  const { view } = state;
  return useCallback(
    (item: ReviewItemFieldsFragment) => isReviewItemListed(item, view, { myId, isReportAuthor, repliesByParentId }),
    [view, myId, isReportAuthor, repliesByParentId],
  );
};
